import useApi from '../hooks/useApi';
import { useState, useEffect } from 'react';
import apiClient from './api-client';

// ============================================================================
// API PATH CONSTANTS
// ============================================================================

const API_BASE = '/audit-logs';
const API_PATHS = {
    BASE: API_BASE,
    BY_ID: (id: number) => `${API_BASE}/${id}`,
    STATISTICS: `${API_BASE}/statistics`,
    RELATED: (correlationId: string) => `${API_BASE}/related/${correlationId}`,
} as const;

// ============================================================================
// AUDIT LOGS INTERFACES
// ============================================================================

export interface AuditLogFilters {
    page?: number;
    pageSize?: number;
    startDate?: string;
    endDate?: string;
    domain?: string;
    accessType?: string;
    action?: string;
    status?: 'SUCCESS' | 'FAILURE' | 'ALL';
    search?: string;
    clientId?: number;
    dpId?: number;
}

export interface AuditLogEvent {
    EventId: number;
    Domain: string;
    DPId?: number;
    ClientId: number;
    AccessType: string;
    Action: string;
    Resource: string;
    IPAddress?: string;
    UserAgent?: string;
    SessionId?: string;
    EventDate: string;
    Success: boolean;
    ErrorMessage?: string;
    AdditionalData?: any;
    
    // Domain-specific fields
    EmailType?: string;
    ToAddress?: string;
    Subject?: string;
    TemplateKey?: string;
    MessageId?: string;
    Provider?: string;
    DeliveredDate?: string;
    RequestId?: number;
    ConsentHeaderId?: number;
    VaultId?: number;
    UserId?: number;
    AccessReason?: string;
    
    // Computed fields
    Status: 'SUCCESS' | 'FAILURE';
    DomainDescription: string;
    ActionDescription: string;
    ResourceDescription: string;
}

export interface AuditLogsResponse {
    events: AuditLogEvent[];
    totalCount: number;
    totalPages: number;
    currentPage: number;
    pageSize: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
}

export interface AuditStatistics {
    statistics: Array<{
        Domain: string;
        EventCount: number;
        SuccessCount: number;
        FailureCount: number;
        FirstEvent: string;
        LastEvent: string;
    }>;
    period: {
        startDate: string | null;
        endDate: string | null;
    };
}

export interface RelatedEventsResponse {
    correlationId: string;
    events: AuditLogEvent[];
    count: number;
}

// ============================================================================
// AUDIT LOGS SERVICE
// ============================================================================

export const auditLogsService = {
    /**
     * Get unified audit events with filtering and pagination
     */
    getAuditLogs: async (filters: AuditLogFilters = {}): Promise<AuditLogsResponse> => {
        const queryParams = new URLSearchParams();
        
        // Add filters to query params
        Object.entries(filters).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                queryParams.append(key, value.toString());
            }
        });

        const response = await apiClient.get<AuditLogsResponse>(`${API_PATHS.BASE}?${queryParams.toString()}`);
        return response;
    },

    /**
     * Get detailed information about a specific audit event
     */
    getAuditEventDetails: async (eventId: number): Promise<AuditLogEvent> => {
        const response = await apiClient.get<AuditLogEvent>(API_PATHS.BY_ID(eventId));
        return response;
    },

    /**
     * Get all audit events related to a specific correlation ID
     */
    getRelatedAuditEvents: async (correlationId: string, limit = 50): Promise<RelatedEventsResponse> => {
        const response = await apiClient.get<RelatedEventsResponse>(`${API_PATHS.RELATED(encodeURIComponent(correlationId))}?limit=${limit}`);
        return response;
    },

    /**
     * Get audit statistics for dashboard
     */
    getAuditStatistics: async (startDate?: string, endDate?: string, clientId?: number): Promise<AuditStatistics> => {
        const queryParams = new URLSearchParams();
        
        if (startDate) queryParams.append('startDate', startDate);
        if (endDate) queryParams.append('endDate', endDate);
        if (clientId) queryParams.append('clientId', clientId.toString());

        const response = await apiClient.get<AuditStatistics>(`${API_PATHS.STATISTICS}?${queryParams.toString()}`);
        return response;
    },

    /**
     * Export audit logs to CSV
     */
    exportAuditLogsToCSV: async (filters: AuditLogFilters = {}): Promise<Blob> => {
        // Get all records (no pagination for export)
        const exportFilters = { ...filters, pageSize: 10000 };
        const data = await auditLogsService.getAuditLogs(exportFilters);
        
        // Convert to CSV
        const csvHeaders = [
            'Event ID',
            'Timestamp',
            'Domain',
            'Action',
            'Resource',
            'User/DP ID',
            'IP Address',
            'Status',
            'Error Message',
            'Additional Data'
        ];

        const csvRows = data.events.map(event => [
            event.EventId,
            new Date(event.EventDate).toISOString(),
            event.DomainDescription,
            event.ActionDescription,
            event.ResourceDescription,
            event.DPId || event.UserId || 'N/A',
            event.IPAddress || 'N/A',
            event.Status,
            event.ErrorMessage || 'N/A',
            event.AdditionalData ? JSON.stringify(event.AdditionalData) : 'N/A'
        ]);

        const csvContent = [csvHeaders, ...csvRows]
            .map(row => row.map(field => `"${field}"`).join(','))
            .join('\n');

        return new Blob([csvContent], { type: 'text/csv' });
    },

    /**
     * Export audit logs to JSON
     */
    exportAuditLogsToJSON: async (filters: AuditLogFilters = {}): Promise<Blob> => {
        // Get all records (no pagination for export)
        const exportFilters = { ...filters, pageSize: 10000 };
        const data = await auditLogsService.getAuditLogs(exportFilters);
        
        const jsonContent = JSON.stringify(data, null, 2);
        return new Blob([jsonContent], { type: 'application/json' });
    }
};

// ============================================================================
// REACT HOOKS FOR AUDIT LOGS
// ============================================================================

/**
 * Hook for fetching audit logs with loading and error states
 */
export const useAuditLogs = (filters: AuditLogFilters = {}) => {
    const [data, setData] = useState<AuditLogsResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    const fetchData = async () => {
        try {
            setLoading(true);
            setError(null);
            const result = await auditLogsService.getAuditLogs(filters);
            setData(result);
        } catch (err) {
            setError(err as Error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [JSON.stringify(filters)]);

    const refetch = () => {
        fetchData();
    };

    return { data, loading, error, refetch };
};

/**
 * Hook for fetching audit event details
 */
export const useAuditEventDetails = (eventId: number | null) => {
    const [data, setData] = useState<AuditLogEvent | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    useEffect(() => {
        if (!eventId) {
            setData(null);
            return;
        }

        const fetchData = async () => {
            try {
                setLoading(true);
                setError(null);
                const result = await auditLogsService.getAuditEventDetails(eventId);
                setData(result);
            } catch (err) {
                setError(err as Error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [eventId]);

    return { data, loading, error };
};

/**
 * Hook for fetching related audit events
 */
export const useRelatedAuditEvents = (correlationId: string | null, limit = 50) => {
    const [data, setData] = useState<RelatedEventsResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    useEffect(() => {
        if (!correlationId) {
            setData(null);
            return;
        }

        const fetchData = async () => {
            try {
                setLoading(true);
                setError(null);
                const result = await auditLogsService.getRelatedAuditEvents(correlationId, limit);
                setData(result);
            } catch (err) {
                setError(err as Error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [correlationId, limit]);

    return { data, loading, error };
};

/**
 * Hook for fetching audit statistics
 */
export const useAuditStatistics = (startDate?: string, endDate?: string, clientId?: number) => {
    const [data, setData] = useState<AuditStatistics | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                setError(null);
                const result = await auditLogsService.getAuditStatistics(startDate, endDate, clientId);
                setData(result);
            } catch (err) {
                setError(err as Error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [startDate, endDate, clientId]);

    return { data, loading, error };
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Get domain icon based on domain type
 */
export const getDomainIcon = (domain: string): string => {
    const iconMap: { [key: string]: string } = {
        'DP_PORTAL': 'person',
        'EMAIL': 'email',
        'VAULT_ACCESS': 'vault',
        'DP_VAULT_ACCESS': 'person_pin',
        'TPP_DELETION': 'business',
        'CONSENT_RETENTION': 'schedule',
        'KAVACH_ADMIN': 'admin_panel_settings',
        'KAVACH_OPS': 'engineering',
        'SYSTEM': 'computer',
        'event-propagation': 'send',
        'RIGHTS_REQUEST': 'assignment'
    };
    return iconMap[domain] || 'help_outline';
};

/**
 * Get status color based on success/failure
 */
export const getStatusColor = (success: boolean): string => {
    return success ? 'success' : 'error';
};

/**
 * Format date for display
 */
export const formatEventDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
    });
};

/**
 * Truncate text for display
 */
export const truncateText = (text: string, maxLength = 50): string => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
};

/**
 * Extract correlation IDs from additional data
 */
export const extractCorrelationIds = (additionalData: any): string[] => {
    const correlationIds: string[] = [];
    
    if (additionalData) {
        if (additionalData.requestId) correlationIds.push(`Request: ${additionalData.requestId}`);
        if (additionalData.consentHeaderId) correlationIds.push(`Consent: ${additionalData.consentHeaderId}`);
        if (additionalData.purposeId) correlationIds.push(`Purpose: ${additionalData.purposeId}`);
        if (additionalData.messageId) correlationIds.push(`Message: ${additionalData.messageId}`);
    }
    
    return correlationIds;
};
